import type { FirstGoalType } from './first-goal';
import type { PlayerPosition } from '../models/tile-models';

// Translation keys for the backend fixture enums (PGFixtureState, PGFixtureRoundType,
// PGFixtureMatchType). The German copy lives in the tenant's `assets/i18n/<lang>.json`
// under `pg.fixture.roundType.*` — the shared core only knows the key.

export type FixtureRoundType =
  | 'QUALIFIER_ROUND'
  | 'ROUND'
  | 'PLAY_OFFS'
  | 'ROUND_OF_32'
  | 'ROUND_OF_16'
  | 'QUARTER_FINALS'
  | 'SEMI_FINALS'
  | 'THIRD_AND_FOURTH_PLACE'
  | 'FINAL';

/** Every round type the backend can send, in bracket order. */
export const FIXTURE_ROUND_TYPES: readonly FixtureRoundType[] = [
  'QUALIFIER_ROUND',
  'ROUND',
  'PLAY_OFFS',
  'ROUND_OF_32',
  'ROUND_OF_16',
  'QUARTER_FINALS',
  'SEMI_FINALS',
  'THIRD_AND_FOURTH_PLACE',
  'FINAL',
];

/** Translation key for a round type, e.g. `pg.fixture.roundType.QUARTER_FINALS`. */
export function fixtureRoundTypeKey(type: FixtureRoundType): string {
  return `pg.fixture.roundType.${type}`;
}

/** Whether the backend value is a round type the UI knows a label for. */
export function isFixtureRoundType(value: string | null | undefined): value is FixtureRoundType {
  return !!value && (FIXTURE_ROUND_TYPES as readonly string[]).includes(value);
}

/**
 * Every additional-question type the UI can render, in the order the backend enum lists
 * them. Single source of truth: {@link AdditionalQuestionType} is derived from it and
 * {@link isAdditionalQuestionType} validates wire values against it, so a new backend
 * type only has to be added here.
 */
export const ADDITIONAL_QUESTION_TYPES = [
  'MATCH_RESULT',
  'FIRST_GOAL_SCORER',
  'LINEUP',
  'SHOTS_ON_GOAL',
  'CORNERS',
  'MATCH_MINUTE_OF_FIRST_GOAL',
  'BALL_POSSESSION',
] as const;

/** The fixture's "additional" (bonus) question — which one it is decides the tip input. */
export type AdditionalQuestionType = (typeof ADDITIONAL_QUESTION_TYPES)[number];

/**
 * Whether the backend value is an additional-question type the UI knows how to render.
 * The backend enum can grow ahead of the frontend, so every value coming off the wire
 * goes through this guard instead of a blind `as AdditionalQuestionType` cast.
 */
export function isAdditionalQuestionType(value: string | null | undefined): value is AdditionalQuestionType {
  return !!value && (ADDITIONAL_QUESTION_TYPES as readonly string[]).includes(value);
}

/**
 * Every fixture lifecycle state the backend can send (`PGFixtureState`). The mapper
 * derives the three UI states from it, so a value missing here would silently fall
 * through to the legacy `finished`/`editable` derivation instead of being reported.
 */
export const FIXTURE_STATES = ['PLANNED', 'ONGOING', 'FINISHED', 'POSTPONED', 'ABANDONED', 'CANCELLED'] as const;

/** The fixture's lifecycle state on the wire — not to be confused with `FixtureStatus` (the UI's three states). */
export type FixtureState = (typeof FIXTURE_STATES)[number];

/** Whether the backend value is a fixture state the UI knows how to interpret. */
export function isFixtureState(value: string | null | undefined): value is FixtureState {
  return !!value && (FIXTURE_STATES as readonly string[]).includes(value);
}

/**
 * Every squad position the backend can send. {@link PlayerPosition} is the model's own
 * union; this array is what wire values are validated against, so both stay in sync.
 */
export const PLAYER_POSITIONS: readonly PlayerPosition[] = ['goalkeeper', 'defender', 'midfielder', 'attacker'];

/** Whether the backend value is a squad position the UI knows how to render. */
export function isPlayerPosition(value: string | null | undefined): value is PlayerPosition {
  return !!value && (PLAYER_POSITIONS as readonly string[]).includes(value);
}

/** Every first-goal kind the backend can send; the dial maps exactly these onto its ordinals. */
export const FIRST_GOAL_TYPES: readonly FirstGoalType[] = [
  'NO_GOAL',
  'IN_REGULAR_TIME',
  'ADDITIONAL_TIME_1ST_HALF',
  'ADDITIONAL_TIME_2ND_HALF',
];

/** Whether the backend value is a first-goal kind the dial can represent. */
export function isFirstGoalType(value: string | null | undefined): value is FirstGoalType {
  return !!value && (FIRST_GOAL_TYPES as readonly string[]).includes(value);
}
